import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as config from '@/lib-backend/methods/config';
import { Category, RealmConfig } from '@/lib-backend';

@Injectable({ providedIn: 'root' })
export class CategoryService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly categories = signal<Category[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    async loadCategories(): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(config.listCategories());
            this.categories.set(resp.Categories);
        } catch (e) {
            this.error.set('Failed to load categories.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async addCategory(
        params: config.AddCategoryRequest
    ): Promise<{ result: config.AddCategoryResult; category: Category | null }> {
        try {
            const resp = await this.api.client.call(config.addCategory(params));
            if (resp.Result === 'Ok') {
                await this.loadCategories();
            }
            return { result: resp.Result, category: resp.Category };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', category: null };
        }
    }

    async updateCategory(
        params: config.UpdateCategoryRequest
    ): Promise<{ result: config.UpdateCategoryResult; category: Category | null }> {
        try {
            const resp = await this.api.client.call(config.updateCategory(params));
            if (resp.Result === 'Ok') {
                await this.loadCategories();
            }
            return { result: resp.Result, category: resp.Category };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', category: null };
        }
    }

    async changeRealmCategory(
        params: config.ChangeRealmCategoryRequest
    ): Promise<{ result: config.ChangeRealmCategoryResult; config: RealmConfig | null }> {
        try {
            const resp = await this.api.client.call(config.changeRealmCategory(params));
            return { result: resp.Result, config: resp.Config };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', config: null };
        }
    }
}
